import { useState, useRef } from 'react';
import { GiftDTO, useGifts, useUpdateGiftStatus } from '@/hooks/useGifts';
import TitlePage from '@/components/TitlePage';
import { Column, TableComponent } from '@/components/TableComponent';
import CreateUpdate from './components/createUpdate';
import { Input } from '@/components/ui/input';
import Delete from './components/delete';
import { debounce } from 'lodash';
import LoadingCircle from '@/components/LoadingCircle';
import { useProfileContext } from '@/hooks/useProfileContext';
import {
  checkIsPermissionWithScreen,
  formatCurrency,
  formatCurrencyWithoutSymbol,
  renderStatusText,
} from '@/lib/utils';
import { Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Select } from '@/components/ui/select';
import { SupplierDTO, useSupplier } from '@/hooks/useSuppliers';
import TextInActive from '@/components/TextInActive';
import { GiftStatus } from '@/api/gifts';
import { Switch } from '@/components/ui/switch';

const ManageGifts = () => {
  const { profile } = useProfileContext();
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const { data: suppliersData } = useSupplier({ search: '', taxCode: '' });
  const { data, isLoading, isFetching } = useGifts({
    name: search,
    id: supplierId,
  });

  const { mutate: changeStatusGift } = useUpdateGiftStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingCircle />
      </div>
    );
  }

  const handleChangeStatus = (item: GiftDTO) => {
    changeStatusGift({
      id: item.id,
      status:
        item.status === GiftStatus.ACTIVE
          ? GiftStatus.INACTIVE
          : GiftStatus.ACTIVE,
    });
  };

  const columns: Column<GiftDTO>[] = [
    {
      header: 'STT',
      cell: (_item, index: number) => {
        return <div>{index + 1}</div>;
      },
    },
    {
      header: 'Tên quà tặng',
      accessorKey: 'name',
      cell: (item) => {
        return (
          <div className="font-medium text-luxury-900 w-[300px] whitespace-pre-line break-words">
            {item.name}
          </div>
        );
      },
    },
    {
      header: 'Trạng thái',
      accessorKey: 'status',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            <Switch
              defaultChecked={item.status === GiftStatus.ACTIVE}
              onCheckedChange={() => {
                handleChangeStatus(item);
              }}
            />

            <span className={renderStatusText(item.status).className}>
              {renderStatusText(item.status).label}
            </span>
          </div>
        );
      },
    },
    {
      header: 'Mô tả',
      accessorKey: 'description',
      cell: (item) => {
        return (
          <div className="w-[300px] whitespace-pre-line break-words">
            {item.description}
          </div>
        );
      },
    },
    {
      header: 'Số lượng còn lại',
      accessorKey: 'remainingQuantity',
      cell: (item) => {
        return (
          <div>
            {formatCurrencyWithoutSymbol(Number(item.remainingQuantity))}
          </div>
        );
      },
    },
    {
      header: 'Đơn giá (VNĐ)',
      cell: (item) => {
        return formatCurrency(Number(item.unitPrice));
      },
    },
    {
      header: 'Nhà cung cấp',
      cell: (item) => {
        return (
          <div>
            {item.supplier.name}
            <TextInActive status={item.supplier.status} />
          </div>
        );
      },
    },
    {
      header: 'Thao tác',
      cell: (item) => {
        return (
          <div className="flex items-center gap-2">
            {checkIsPermissionWithScreen(
              'gifts',
              'update',
              profile?.role?.permissions
            ) && (
              <CreateUpdate
                gift={item}
                suppliers={suppliersData?.suppliers || []}
              />
            )}
            {checkIsPermissionWithScreen(
              'gifts',
              'delete',
              profile?.role?.permissions
            ) && <Delete gift={item} />}
          </div>
        );
      },
    },
  ];

  const debouncedSearch = debounce((value: string) => {
    setSearch(value);
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="text-2xl  text-luxury-900">
            <TitlePage />
          </div>
        </div>
        {checkIsPermissionWithScreen(
          'gifts',
          'create',
          profile?.role?.permissions
        ) && <CreateUpdate suppliers={suppliersData?.suppliers || []} />}
      </div>

      <div className="flex items-center justify-end bg-white p-4 border-b border-luxury-200 shadow-sm gap-4">
        <div className="flex items-center gap-2">
          <Select
            value={supplierId}
            onValueChange={(value) => setSupplierId(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Chọn nhà cung cấp" />
            </SelectTrigger>
            <SelectContent className="border-luxury-200">
              {suppliersData?.suppliers.map((supplier: SupplierDTO) => (
                <SelectItem key={supplier.id} value={supplier.id.toString()}>
                  {supplier.name}
                  <TextInActive status={supplier.status} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Input
            ref={searchRef}
            placeholder="Tìm kiếm theo tên quà tặng..."
            className="w-[250px] luxury-input"
            onChange={handleSearch}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="luxury-button-outline"
            onClick={() => {
              setSupplierId('');
              setSearch('');
              if (searchRef.current) {
                searchRef.current.value = '';
              }
            }}
          >
            <Filter className="w-4 h-4 mr-2" />
            Xoá lọc
          </Button>
        </div>
      </div>

      <div className="p-4 relative">
        {isFetching && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
            <LoadingCircle />
          </div>
        )}
        <TableComponent
          data={data?.gifts || []}
          columns={columns}
          pageSize={0}
          totalPages={0}
          onPageChange={() => {}}
          currentPage={0}
          isPagination={false}
          className="luxury-table"
        />
      </div>
    </div>
  );
};

export default ManageGifts;
